import { addDays, format, isValid, startOfDay } from "date-fns";
import { FormValues } from "./SettingsDialog";
import {
  CommonWorkTimeBankSettingMode,
  EditWorkTimeBankSettings,
  Employment,
  FormattedWorkTimeBankSetting,
} from "../../../../employment/employmentApi";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";

export const validateFromDate = (
  value: Date,
  toDate: Date | null,
  employmentStartDate: Date,
  employmentEndDate?: Date,
) => {
  if (!isValid(new Date(value))) {
    return "invalidDate";
  }
  const fromDay = startOfDay(value).getTime();
  if (
    fromDay < startOfDay(employmentStartDate).getTime() ||
    (employmentEndDate && fromDay > startOfDay(employmentEndDate).getTime())
  ) {
    return "dateMustBeInsideEmploymentPeriod";
  }
  if (toDate && fromDay > startOfDay(toDate).getTime()) {
    return "fromDateMustBeLessThanToDate";
  }
  return true;
};

export const validateToDate = (
  value: Date,
  fromDate: Date,
  nextFromDate: Date | null,
  employmentStartDate: Date,
  employmentEndDate?: Date,
) => {
  if (!value) return true;
  if (!isValid(new Date(value))) {
    return "invalidDate";
  }

  const fromDateFormatted = startOfDay(fromDate).getTime();
  const toDate = startOfDay(value).getTime();
  const nextFromDateFormatted = nextFromDate
    ? startOfDay(nextFromDate).getTime()
    : undefined;
  const employmentStartDateFormatted =
    startOfDay(employmentStartDate).getTime();
  const employmentEndDateFormatted = employmentEndDate
    ? startOfDay(employmentEndDate).getTime()
    : undefined;

  if (
    toDate < employmentStartDateFormatted ||
    (employmentEndDateFormatted && toDate > employmentEndDateFormatted)
  ) {
    return "dateMustBeInsideEmploymentPeriod";
  }
  if (toDate < fromDateFormatted) {
    return "toDateMustBeGreaterThanFromDate";
  }
  if (nextFromDateFormatted && toDate >= nextFromDateFormatted) {
    return "toDateMustBeLessThanNextFromDate";
  }
  return true;
};

const constructSettings = (
  values: FormValues,
  employmentEnd?: string,
): FormattedWorkTimeBankSetting[] => {
  // Check the date values and see if mode=null rows need to be generated
  // for the possible gaps between the given settings.
  return values.workTimeBankSettings.flatMap((setting, index) => {
    const nextSetting = values.workTimeBankSettings[index + 1];
    if (nextSetting) {
      const nextFromDate = startOfDay(nextSetting.from);
      const currentToDate = startOfDay(setting.to || addDays(nextFromDate, -1));
      if (addDays(currentToDate, 1).getTime() !== nextFromDate.getTime()) {
        const additionalSetting: FormattedWorkTimeBankSetting = {
          from: addDays(currentToDate, 1),
          to: addDays(nextFromDate, -1),
          mode: CommonWorkTimeBankSettingMode.NOT_SET,
        };
        return [setting, additionalSetting];
      }
    } else {
      // If last setting end date is not employment end date, add a new setting
      const calculatedEnd = employmentEnd || "";
      if (
        setting.to &&
        startOfDay(setting.to).getTime() !==
          startOfDay(new Date(calculatedEnd)).getTime()
      ) {
        const additionalSetting: FormattedWorkTimeBankSetting = {
          from: addDays(setting.to, 1),
          to: !!calculatedEnd ? new Date(calculatedEnd) : null,
          mode: CommonWorkTimeBankSettingMode.NOT_SET,
        };
        return [setting, additionalSetting];
      }
      return [setting];
    }
    return [setting];
  });
};

export const constructPayload = (
  values: FormValues,
  employmentAdditionalInfo: Employment,
): EditWorkTimeBankSettings => {
  const settings = constructSettings(
    values,
    employmentAdditionalInfo.details.agreedEnd,
  );
  return {
    employmentId: employmentAdditionalInfo.employmentId,
    version: employmentAdditionalInfo.details.version,
    settings: settings.map((setting) => ({
      from: format(setting.from, isoFormat),
      mode:
        setting.mode === CommonWorkTimeBankSettingMode.NOT_SET
          ? null
          : setting.mode,
      ...(!!setting.min && { min: setting.min }),
      ...(!!setting.max && { max: setting.max }),
    })),
  };
};
